import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Pagination } from '../components/ui';

interface AiJob {
  id: string; provider: string; model: string; prompt: string;
  status: string; progress: number; error: string | null; url: string | null;
  createdAt: string; updatedAt: string; aiJobId: string | null;
}

const badge = (s: string) =>
  s === 'COMPLETED' || s === 'SUCCEEDED' ? 'badge badge--success'
  : s === 'FAILED' || s === 'CANCELLED' ? 'badge badge--danger'
  : s === 'PROCESSING' || s === 'SUBMITTED' ? 'badge badge--warning'
  : 'badge badge--neutral';

export function VideoJobsPage() {
  const [jobs, setJobs] = useState<AiJob[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const load = useCallback(async () => {
    try { setJobs(await api.videoJobs.list()); } catch { /* noop */ }
  }, []);

  useEffect(() => { load().catch(() => undefined); }, [load]);

  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => load().catch(() => undefined), 5000);
    return () => clearInterval(t);
  }, [auto, load]);

  const poll = async (id: string) => {
    setBusy(id);
    setMsg(null);
    try {
      const r = await api.videoJobs.poll(id);
      setMsg(`Job ${id}: ${r.status}`);
      await load();
    } catch (e) {
      setMsg((e as Error).message);
    } finally { setBusy(null); }
  };

  const active = jobs.filter((j) => j.status === 'PROCESSING' || j.status === 'SUBMITTED').length;

  return (
    <div>
      <h2>Video Jobs (AI)</h2>
      <p className="muted">Generacje wideo są asynchroniczne: submit → poll → plik. Odświeżanie co 5 s: {auto ? 'ON' : 'OFF'} · aktywne: {active}</p>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> auto-poll
      </label>
      {msg && <div className="alert" style={{ margin: '10px 0' }}>{msg}</div>}
      <div className="card">
        <div className="card__header">AI video jobs</div>
        <table>
          <thead><tr><th>Kiedy</th><th>Provider</th><th>Model</th><th>Prompt</th><th>Status</th><th>Progress</th><th /></tr></thead>
          <tbody>
            {jobs.slice((page - 1) * pageSize, page * pageSize).map((j) => (
              <tr key={j.id}>
                <td className="muted" style={{ fontSize: 12 }}>{j.createdAt?.slice(0, 16)}</td>
                <td>{j.provider}</td>
                <td>{j.model}</td>
                <td style={{ maxWidth: 260 }}><span className="muted" style={{ fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.prompt}</span></td>
                <td><span className={badge(j.status)}>{j.status}</span></td>
                <td>{j.progress}%</td>
                <td>
                  <button className="btn btn--sm" onClick={() => poll(j.id)} disabled={busy === j.id}>
                    {busy === j.id ? 'Polling…' : 'Poll'}
                  </button>
                  {j.status === 'SUCCEEDED' && (
                    <a className="btn btn--sm btn--primary" href={`/api/media/video/jobs/${j.id}/content`} target="_blank" rel="noreferrer" style={{ marginLeft: 6 }}>
                      ▶ Watch
                    </a>
                  )}
                </td>
              </tr>
            ))}
            {jobs.length === 0 && <tr><td colSpan={7} className="muted">Brak AI video jobów.</td></tr>}
          </tbody>
        </table>
        <Pagination page={page} pageSize={pageSize} total={jobs.length} onChange={setPage} />
      </div>
    </div>
  );
}
